import { createMediaQuery } from "@solid-primitives/media";
import { createElementSize } from "@solid-primitives/resize-observer";
import { type JSX, Show, createEffect, createSignal, untrack } from "solid-js";
import { isServer } from "solid-js/web";

import styles from "~/components/player/SmartMarquee.module.scss";

interface SmartMarqueeProps {
  readonly fadePadding?: JSX.CSSProperties["padding"];
  readonly repeaterMargin?: JSX.CSSProperties["padding"];
  readonly interval?: number;
  readonly speed?: number;
  readonly children: JSX.Element;
}

export default function SmartMarquee(props: SmartMarqueeProps) {
  const [marqueeRef, setMarqueeRef] = createSignal<HTMLDivElement>();
  const [textRef, setTextRef] = createSignal<HTMLSpanElement>();
  const [mimicRef, setMimicRef] = createSignal<HTMLSpanElement>();
  const [isMarqueeActive, setIsMarqueeActive] = createSignal(false);
  const [offset, setOffset] = createSignal(0);
  const canHover = createMediaQuery("(hover: hover)");

  const marqueeSize = createElementSize(marqueeRef);
  const textSize = createElementSize(textRef);

  let timeout = -1;
  let frame = -1;
  let startTimestamp = -1;
  let storedOffset = 0;
  let maxOffset = 0;

  const pause = () => {
    cancelAnimationFrame(frame);
    clearTimeout(timeout);
    startTimestamp = -1;
    storedOffset = untrack(offset);
  };

  const stop = () => {
    pause();
    storedOffset = 0;
    setOffset(0);
  };

  const runFrame = (force: boolean, originalTimestamp?: number) => {
    const timestamp = originalTimestamp ?? performance.now();

    if (startTimestamp < 0) {
      startTimestamp = timestamp;
      frame = requestAnimationFrame((ts) => {
        runFrame(force, ts);
      });
      return;
    }

    if (!(force || offset())) {
      pause();
      timeout = window.setTimeout(() => {
        runFrame(true);
      }, props.interval ?? 2500);
      return;
    }

    frame = requestAnimationFrame((ts) => {
      runFrame(false, ts);
    });

    const newOffset =
      storedOffset +
      ((timestamp - startTimestamp) * (props.speed ?? 45)) / 1000;

    if (newOffset > maxOffset) {
      setOffset(0);
      return;
    }

    setOffset(newOffset);
  };

  createEffect(() => {
    const mimic = mimicRef();
    if (marqueeSize.width === null || textSize.width === null || !mimic) {
      return;
    }

    stop();
    setIsMarqueeActive(marqueeSize.width < textSize.width);

    maxOffset =
      textSize.width +
      Number.parseInt(getComputedStyle(mimic).paddingInlineStart, 10);

    if (isMarqueeActive()) {
      runFrame(false);
    }
  });

  return (
    <Show when={!isServer} fallback={props.children}>
      <div
        classList={{
          [styles.marquee]: true,
          [styles.enabled]: isMarqueeActive(),
        }}
        style={{
          "--fade-padding": props.fadePadding,
          "--repeater-margin": props.repeaterMargin,
          "--offset": offset(),
        }}
        onPointerEnter={() => {
          if (canHover()) {
            pause();
          }
        }}
        onPointerLeave={() => {
          if (canHover() && isMarqueeActive()) {
            runFrame(false);
          }
        }}
      >
        <div ref={setMarqueeRef} class={styles.scroller}>
          <span ref={setTextRef}>{props.children}</span>
          <span ref={setMimicRef} class={styles.mimic} aria-hidden="true">
            {props.children}
          </span>
        </div>
      </div>
    </Show>
  );
}
